import React, {useEffect, useState} from 'react';
import { Link } from 'react-router-dom';
import { 
  FolderOpen, 
  ArrowRightLeft, 
  Sparkles, 
  Folder, 
  Clock, 
  Layers, 
  ShieldCheck, 
  Apple, 
  ChevronRight,
  Star
} from 'lucide-react';

const HomePage = () => {

  const [link, setLink] = useState('https://shiftfolder.com/ShiftFolder.dmg');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let baseUrl = 'https://shiftfolder.com/ShiftFolder.dmg';
    const finalUrl = `${baseUrl}?${urlParams.toString()}`;
    setLink(finalUrl);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 flex flex-col">
      {/* Navigation */}
      <nav className="bg-white py-4 shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <FolderOpen className="h-8 w-8 text-teal-600" />
              <span className="ml-2 text-2xl font-semibold text-gray-900">ShiftFolder</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-teal-600 transition-colors">Features</a>
              <a href="#testimonials" className="text-gray-600 hover:text-teal-600 transition-colors">Testimonials</a>
              <a href={link} download className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors">Download</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Asymmetric Design */}
      <section className="relative overflow-hidden">
        <div className="container mx-auto px-6 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Organize Your Files Like Never Before
              </h1>
              <div className="h-1 w-20 bg-teal-600 my-6"></div>
              <p className="text-xl text-gray-600 mb-8">
                ShiftFolder helps Mac users organize, manage, and transform their file systems with intelligent automation and beautiful simplicity.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={link} download
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-all flex items-center justify-center"
                >
                  <Apple className="mr-2 h-5 w-5" />
                  Download for Mac
                </a>
                <a 
                  href="#features" 
                  className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-all flex items-center justify-center"
                >
                  Learn More <ChevronRight className="ml-1 h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="order-1 md:order-2 relative">
              <div className="relative bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-6 shadow-xl">
                <div className="absolute inset-0 bg-white/10 rounded-2xl backdrop-blur-sm"></div>
                <div className="relative">
                  <div className="flex items-center mb-3">
                    <div className="h-3 w-3 rounded-full bg-red-500 mr-2"></div>
                    <div className="h-3 w-3 rounded-full bg-yellow-500 mr-2"></div>
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm mb-3">
                    <Folder className="text-white mb-2 h-10 w-10" />
                    <div className="h-2 w-24 bg-white/60 rounded-full mb-2"></div>
                    <div className="h-2 w-16 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm mb-3">
                    <ArrowRightLeft className="text-white mb-2 h-10 w-10" />
                    <div className="h-2 w-20 bg-white/60 rounded-full mb-2"></div>
                    <div className="h-2 w-14 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
                    <Sparkles className="text-white mb-2 h-10 w-10" />
                    <div className="h-2 w-28 bg-white/60 rounded-full mb-2"></div>
                    <div className="h-2 w-12 bg-white/40 rounded-full"></div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 -z-10 h-32 w-32 bg-yellow-400 rounded-full opacity-30 blur-xl"></div>
              <div className="absolute -bottom-8 -left-8 -z-10 h-40 w-40 bg-teal-400 rounded-full opacity-30 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Simplify Your File Management
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              ShiftFolder combines powerful organization tools with an intuitive interface to transform how you work with files on your Mac.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<ArrowRightLeft className="text-teal-600" size={24} />}
              title="Smart File Organization"
              description="Automatically sort and organize files based on type, date, name or custom rules you define."
            />
            <FeatureCard 
              icon={<Clock className="text-teal-600" size={24} />}
              title="Scheduled Operations"
              description="Set up recurring file operations to keep your system organized without manual intervention."
            />
            <FeatureCard 
              icon={<Layers className="text-teal-600" size={24} />}
              title="Batch Processing"
              description="Transform multiple files and folders at once with powerful batch operations."
            />
            <FeatureCard 
              icon={<ShieldCheck className="text-teal-600" size={24} />}
              title="Safe Handling"
              description="Advanced conflict resolution ensures your files are always protected during operations."
            />
            <FeatureCard 
              icon={<Folder className="text-teal-600" size={24} />}
              title="Custom Templates"
              description="Create folder structure templates to instantly set up project directories."
            />
            <FeatureCard 
              icon={<Sparkles className="text-teal-600" size={24} />}
              title="File Preview"
              description="Quick Look integration lets you preview files without opening them."
            />
          </div>
        </div>
      </section>

      {/* Workflow Section - Visual Elements */}
      <section className="py-20 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Transform Your Workflow
              </h2>
              <div className="h-1 w-20 bg-teal-600 mb-6"></div>
              <p className="text-lg text-gray-600 mb-6">
                ShiftFolder seamlessly integrates with macOS to enhance your productivity without disrupting your existing workflow.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-teal-100 flex items-center justify-center mt-1">
                    <span className="text-teal-600 text-sm font-bold">1</span>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Drag and Drop</h3>
                    <p className="text-gray-600">Simply drag files into ShiftFolder to begin organizing.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-teal-100 flex items-center justify-center mt-1">
                    <span className="text-teal-600 text-sm font-bold">2</span>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Set Rules</h3>
                    <p className="text-gray-600">Create custom rules for automatic file processing.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-teal-100 flex items-center justify-center mt-1">
                    <span className="text-teal-600 text-sm font-bold">3</span>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Watch It Work</h3>
                    <p className="text-gray-600">ShiftFolder handles the rest, keeping your Mac organized.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2 relative">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-2xl p-6 transform rotate-1">
                <div className="flex items-center mb-4">
                  <div className="h-3 w-3 rounded-full bg-red-500 mr-2"></div>
                  <div className="h-3 w-3 rounded-full bg-yellow-500 mr-2"></div>
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                </div>
                <div className="flex space-x-3 mb-4">
                  <div className="bg-teal-500/20 rounded-md p-3">
                    <Folder className="h-8 w-8 text-teal-400" />
                  </div>
                  <div className="bg-teal-500/10 rounded-md p-3">
                    <Folder className="h-8 w-8 text-teal-300" />
                  </div>
                  <div className="bg-teal-500/5 rounded-md p-3">
                    <Folder className="h-8 w-8 text-teal-200" />
                  </div>
                </div>
                <div className="bg-white/10 rounded-md p-4 mb-3">
                  <div className="h-2 w-full bg-white/20 rounded-full mb-2"></div>
                  <div className="h-2 w-3/4 bg-white/20 rounded-full mb-2"></div>
                  <div className="h-2 w-1/2 bg-white/20 rounded-full"></div>
                </div>
                <div className="bg-teal-600/20 rounded-md p-4">
                  <div className="h-8 w-8 rounded-md bg-teal-500/30 mb-2"></div>
                  <div className="h-2 w-20 bg-teal-400/40 rounded-full mb-2"></div>
                  <div className="h-2 w-16 bg-teal-400/30 rounded-full"></div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-teal-400 to-emerald-500 h-24 w-24 rounded-full opacity-50 blur-lg -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Mac Users Are Saying
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join thousands of satisfied users who have transformed their file management experience.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <TestimonialCard 
              name="Alex M."
              role="Graphic Designer"
              rating={5}
              testimonial="ShiftFolder has revolutionized how I manage project files. The custom templates save me hours of setup time for each new client."
            />
            <TestimonialCard 
              name="Sarah J."
              role="Software Developer"
              rating={5}
              testimonial="The batch processing capabilities are incredible. I can organize thousands of files with just a few clicks. Best Mac utility I've purchased."
            />
            <TestimonialCard 
              name="David L."
              role="Photographer"
              rating={4}
              testimonial="Managing photo shoots used to be a nightmare until I found ShiftFolder. Now my workflow is smooth and my directories are always organized."
            />
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-20 bg-gradient-to-br from-teal-500 to-emerald-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Mac Experience?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto opacity-90">
            ShiftFolder is available for macOS 11 (Big Sur) and later. Download now and start organizing your files effortlessly.
          </p>
          
          <div className="inline-block bg-white/20 backdrop-blur-sm rounded-xl p-8 shadow-lg">
            <div className="flex items-center justify-center mb-6">
              <Apple size={28} className="text-white mr-3" />
              <span className="text-xl font-medium">macOS App</span>
            </div>
            <a
                href={link} download
              className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-medium text-lg inline-flex items-center transition-all shadow-lg"
            >
              Download ShiftFolder
            </a>
            <p className="mt-4 text-sm opacity-80">Compatible with macOS 11.0+</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-900 text-gray-400">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-6 md:mb-0">
              <FolderOpen className="h-6 w-6 text-teal-500" />
              <span className="ml-2 text-xl font-semibold text-white">ShiftFolder</span>
            </div>
            
            <div className="flex flex-col md:flex-row items-center md:space-x-8 space-y-4 md:space-y-0">
              <a href="#features" className="hover:text-teal-400 transition-colors">Features</a>
              <Link to="/privacy" className="hover:text-teal-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-teal-400 transition-colors">Terms of Service</Link>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm">© {new Date().getFullYear()} ShiftFolder. All rights reserved.</p>
            <p className="text-sm mt-4 md:mt-0">
              <a href="mailto:contact@shiftfolder.com" className="text-teal-400 hover:text-teal-300">
                contact@shiftfolder.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description }) => {
  return (
    <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
      <div className="bg-teal-50 p-3 inline-flex rounded-lg mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2 text-gray-900">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ name, role, rating, testimonial }) => {
  return (
    <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex text-yellow-400 mb-3">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} size={16} fill="currentColor" />
        ))}
      </div>
      <p className="text-gray-700 mb-4">{testimonial}</p>
      <div>
        <h4 className="font-semibold text-gray-900">{name}</h4>
        <p className="text-gray-500 text-sm">{role}</p>
      </div>
    </div>
  );
};

export default HomePage;
